import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import { z } from "zod";
import { Mail, Send, Phone } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "../components/ui/form";
import { useToast } from "../hooks/use-toast";
import { apiRequest } from "../lib/queryClient";

const contactFormSchema = z.object({
  name: z.string().min(2, "Имя должно содержать минимум 2 символа"),
  email: z.string().email("Введите корректный email"),
  project: z.string().min(10, "Описание проекта должно содержать минимум 10 символов"),
  budget: z.string().optional(),
});

type ContactFormData = z.infer<typeof contactFormSchema>;

export default function Contact() {
  const { toast } = useToast();
  // const queryClient = useQueryClient();

  const form = useForm<ContactFormData>({
    resolver: zodResolver(contactFormSchema),
    defaultValues: {
      name: "",
      email: "",
      project: "",
      budget: "",
    },
  });

  const submitContactMutation = useMutation({
    mutationFn: async (data: ContactFormData) => {
      return await apiRequest("POST", "/api/contact", data);
    },
    onSuccess: () => {
      toast({
        title: "Спасибо!",
        description: "Ваш запрос отправлен. Мы свяжемся с вами в ближайшее время.",
      });
      form.reset();
    },
    onError: () => {
      toast({
        title: "Ошибка",
        description: "Не удалось отправить запрос. Попробуйте еще раз.",
        variant: "destructive",
      });
    },
  });

  const onSubmit = (data: ContactFormData) => {
    submitContactMutation.mutate(data);
  };

  return (
    <section id="contact" className="py-20 bg-gradient-to-br from-sprintwise-primary/5 via-sprintwise-secondary/5 to-sprintwise-accent/5">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-sprintwise-dark mb-6" data-testid="contact-title">
            Готовы обсудить <span className="gradient-text">ваш проект?</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto" data-testid="contact-description">
            Расскажите о вашей идее, и мы предложим оптимальное техническое решение
          </p>
        </div>
        
        <div className="grid lg:grid-cols-2 gap-12 items-start">
          <div>
            <div className="bg-white rounded-2xl p-8 shadow-xl">
              <h3 className="text-2xl font-semibold text-sprintwise-dark mb-6" data-testid="contact-form-title">
                Отправить запрос
              </h3>
              
              <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6" data-testid="contact-form">
                  <FormField
                    control={form.control}
                    name="name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Ваше имя *</FormLabel>
                        <FormControl>
                          <Input placeholder="Как к вам обращаться?" {...field} data-testid="input-name" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  
                  <FormField
                    control={form.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Email *</FormLabel>
                        <FormControl>
                          <Input type="email" placeholder="example@company.com" {...field} data-testid="input-email" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  
                  <FormField
                    control={form.control}
                    name="project"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Описание задачи *</FormLabel>
                        <FormControl>
                          <Textarea 
                            placeholder="Расскажите о вашем проекте, целях и требованиях..." 
                            className="resize-none"
                            rows={4}
                            {...field} 
                            data-testid="textarea-project"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  
                  <FormField
                    control={form.control}
                    name="budget"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Ориентировочный бюджет</FormLabel>
                        <Select onValueChange={field.onChange} defaultValue={field.value}>
                          <FormControl>
                            <SelectTrigger data-testid="select-budget">
                              <SelectValue placeholder="Выберите диапазон" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="100-500k">100 000 - 500 000₽</SelectItem>
                            <SelectItem value="500k-1m">500 000₽ - 1 000 000₽</SelectItem>
                            <SelectItem value="1m-3m">1 000 000₽ - 3 000 000₽</SelectItem>
                            <SelectItem value="3m+">От 3 000 000₽</SelectItem>
                            <SelectItem value="discuss">Обсудим отдельно</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  
                  <Button 
                    type="submit" 
                    className="w-full bg-gradient-to-r from-sprintwise-primary to-sprintwise-secondary text-white hover:shadow-lg hover:scale-105 transition-all duration-300"
                    disabled={submitContactMutation.isPending}
                    data-testid="button-submit-contact"
                  >
                    <Send className="mr-2 h-4 w-4" />
                    {submitContactMutation.isPending ? "Отправка..." : "Отправить запрос"}
                  </Button>
                </form>
              </Form>
            </div>
          </div>
          
          <div className="space-y-8">
            <div>
              <h3 className="text-2xl font-semibold text-sprintwise-dark mb-6" data-testid="contact-methods-title">
                Другие способы связи
              </h3>
              
              <div className="space-y-4">
                <div className="flex items-start space-x-4 p-4 bg-white rounded-xl shadow-lg" data-testid="contact-email">
                  <div className="w-12 h-12 bg-gradient-to-r from-sprintwise-primary to-sprintwise-secondary rounded-lg flex items-center justify-center flex-shrink-0">
                    <Mail className="text-white" />
                  </div>
                  <div>
                    <div className="font-semibold text-sprintwise-dark">Email</div>
                    <a 
                      href="mailto:hello@sprintwise.ru" 
                      className="text-gray-600 hover:text-sprintwise-primary transition-colors duration-200 cursor-pointer"
                      data-testid="contact-email-link"
                    >
                      hello@sprintwise.ru
                    </a>
                    <div className="text-sm text-gray-500">Ответим в течение часа</div>
                  </div>
                </div>
                
                <div className="flex items-start space-x-4 p-4 bg-white rounded-xl shadow-lg" data-testid="contact-telegram">
                  <div className="w-12 h-12 bg-gradient-to-r from-sprintwise-secondary to-sprintwise-accent rounded-lg flex items-center justify-center flex-shrink-0">
                    <Send className="text-white" />
                  </div>
                  <div>
                    <div className="font-semibold text-sprintwise-dark">Telegram</div>
                    <a 
                      href="https://t.me/pprosvetov" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-gray-600 hover:text-sprintwise-primary transition-colors duration-200 cursor-pointer"
                      data-testid="contact-telegram-link"
                    >
                      @pprosvetov
                    </a>
                    <div className="text-sm text-gray-500">Быстрый ответ в рабочее время</div>
                  </div>
                </div>
                
                <div className="flex items-start space-x-4 p-4 bg-white rounded-xl shadow-lg" data-testid="contact-phone">
                  <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Phone className="text-white" />
                  </div>
                  <div>
                    <div className="font-semibold text-sprintwise-dark">Телефон</div>
                    <a 
                      href="tel:+79264669322" 
                      className="text-gray-600 hover:text-sprintwise-primary transition-colors duration-200 cursor-pointer"
                      data-testid="contact-phone-link"
                    >
                      +7 (926) 466-93-22
                    </a>
                    <div className="text-sm text-gray-500">Пн-Пт с 10:00 до 19:00 МСК</div>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="relative" data-testid="contact-cta-image">
              <img 
                src="/images/contact-design.jpg" 
                alt="Процесс цифрового дизайна" 
                className="rounded-2xl shadow-xl w-full h-auto" 
              />
              <div className="absolute inset-0 bg-gradient-to-t from-sprintwise-dark/50 to-transparent rounded-2xl flex items-end p-6">
                <div className="text-white">
                  <div className="text-lg font-semibold mb-1">Начнем уже сегодня</div>
                  <div className="text-sm opacity-90">Первая консультация — бесплатно</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
